import { createContext, useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';

export interface ValidationContextValue {
  isProjectNameValid: boolean;
  setIsProjectNameValid: Dispatch<SetStateAction<boolean>>;
  projectNameErrorMessage: string;
  setProjectNameErrorMessage: Dispatch<SetStateAction<string>>;
  isProjectMunicipalityValid: boolean;
  setIsProjectMunicipalityValid: Dispatch<SetStateAction<boolean>>;
  projectMunicipalityErrorMessage: string;
  setProjectMunicipalityErrorMessage: Dispatch<SetStateAction<string>>;
  showProjectValidationErrors: boolean;
  setShowProjectValidationErrors: Dispatch<SetStateAction<boolean>>;
  clearValidationForProjectName: () => void;
  clearValidationForProjectMunicipality: () => void;
  clearProjectValidationErrors: () => void;

  isGridNameValid: boolean;
  setIsGridNameValid: Dispatch<SetStateAction<boolean>>;
  gridNameErrorMessage: string;
  setGridNameErrorMessage: Dispatch<SetStateAction<string>>;
  isGridAnalysisAreaValid: boolean;
  setIsGridAnalysisAreaValid: Dispatch<SetStateAction<boolean>>;
  gridAnalysisAreaErrorMessage: string;
  setGridAnalysisAreaErrorMessage: Dispatch<SetStateAction<string>>;
  isGridCoordinateSystemValid: boolean;
  setIsGridCoordinateSystemValid: Dispatch<SetStateAction<boolean>>;
  clearValidationForGridName: () => void;
  clearValidationForGridAnalysisArea: () => void;
  clearValidationForGridCoordinateSystem: () => void;

  isGridTerrainFileValid: boolean;
  setIsGridTerrainFileValid: Dispatch<SetStateAction<boolean>>;
  gridTerrainFileErrorMessage: string;
  setGridTerrainFileErrorMessage: Dispatch<SetStateAction<string>>;
  clearValidationForGridTerrainFile: () => void;

  isGridSceneFileValid: boolean;
  setIsGridSceneFileValid: Dispatch<SetStateAction<boolean>>;
  gridSceneFileErrorMessage: string;
  setGridSceneFileErrorMessage: Dispatch<SetStateAction<string>>;
  clearValidationForGridSceneFile: () => void;

  isGeodataNameValid: boolean;
  setIsGeodataNameValid: Dispatch<SetStateAction<boolean>>;
  geodataNameErrorMessage: string;
  setGeodataNameErrorMessage: Dispatch<SetStateAction<string>>;

  isGeodataCustomPolygonValid: boolean;
  setIsGeodataCustomPolygonValid: Dispatch<SetStateAction<boolean>>;
  geodataCustomPolygonErrorMessage: string;
  setGeodataCustomPolygonErrorMessage: Dispatch<SetStateAction<string>>;

  clearValidationForGeodataName: () => void;
  clearValidationForGeodataCustomPolygon: () => void;

  isSimulationNameValid: boolean;
  setIsSimulationNameValid: Dispatch<SetStateAction<boolean>>;
  simulationNameErrorMessage: string;
  setSimulationNameErrorMessage: Dispatch<SetStateAction<string>>;
  isSimulationNoiseSourcesValid: boolean;
  setIsSimulationNoiseSourcesValid: Dispatch<SetStateAction<boolean>>;
  simulationNoiseSourcesErrorMessage: string;
  setSimulationNoiseSourcesErrorMessage: Dispatch<SetStateAction<string>>;
  clearValidationForSimulationName: () => void;
  clearValidationForSimulationNoiseSources: () => void;
  isSimulationWindSimulationsValid: boolean;
  setIsSimulationWindSimulationsValid: Dispatch<SetStateAction<boolean>>;
  simulationWindSimulationsErrorMessage: string;
  setSimulationWindSimulationsErrorMessage: Dispatch<SetStateAction<string>>;
  clearValidationForSimulationWindSimulations: () => void;
}

export const ValidationContext = createContext<ValidationContextValue>({} as ValidationContextValue);

interface ValidationContextProviderProps {
  children: ReactNode;
}

const ValidationContextProvider = (props: ValidationContextProviderProps) => {
  const [isProjectNameValid, setIsProjectNameValid] = useState(true);
  const [projectNameErrorMessage, setProjectNameErrorMessage] = useState('');

  const [isProjectMunicipalityValid, setIsProjectMunicipalityValid] = useState(false);
  const [projectMunicipalityErrorMessage, setProjectMunicipalityErrorMessage] = useState('');
  const [showProjectValidationErrors, setShowProjectValidationErrors] = useState(false);

  const [isGridNameValid, setIsGridNameValid] = useState(true);
  const [gridNameErrorMessage, setGridNameErrorMessage] = useState('');

  const [isGridAnalysisAreaValid, setIsGridAnalysisAreaValid] = useState(false);
  const [gridAnalysisAreaErrorMessage, setGridAnalysisAreaErrorMessage] = useState('*please define an analysis area');
  const [isGridCoordinateSystemValid, setIsGridCoordinateSystemValid] = useState(true);

  const [isGridTerrainFileValid, setIsGridTerrainFileValid] = useState(true);
  const [gridTerrainFileErrorMessage, setGridTerrainFileErrorMessage] = useState('');

  const [isGridSceneFileValid, setIsGridSceneFileValid] = useState(true);
  const [gridSceneFileErrorMessage, setGridSceneFileErrorMessage] = useState('');

  const [isGeodataNameValid, setIsGeodataNameValid] = useState(true);
  const [geodataNameErrorMessage, setGeodataNameErrorMessage] = useState('');

  const [isGeodataCustomPolygonValid, setIsGeodataCustomPolygonValid] = useState(true);
  const [geodataCustomPolygonErrorMessage, setGeodataCustomPolygonErrorMessage] = useState('');

  const [isSimulationNameValid, setIsSimulationNameValid] = useState(true);
  const [simulationNameErrorMessage, setSimulationNameErrorMessage] = useState('');

  const [isSimulationNoiseSourcesValid, setIsSimulationNoiseSourcesValid] = useState(true);
  const [simulationNoiseSourcesErrorMessage, setSimulationNoiseSourcesErrorMessage] = useState('');

  const [isSimulationWindSimulationsValid, setIsSimulationWindSimulationsValid] = useState(true);
  const [simulationWindSimulationsErrorMessage, setSimulationWindSimulationsErrorMessage] = useState('');

  const clearValidationForProjectName = () => {
    setIsProjectNameValid(true);
    setProjectNameErrorMessage('');
  };

  const clearValidationForProjectMunicipality = () => {
    setIsProjectMunicipalityValid(false);
    setProjectMunicipalityErrorMessage('');
  };
  const clearProjectValidationErrors = () => {
    setShowProjectValidationErrors(false);
  };

  const clearValidationForGridName = () => {
    setIsGridNameValid(true);
    setGridNameErrorMessage('');
  };

  const clearValidationForGridAnalysisArea = () => {
    setIsGridAnalysisAreaValid(false);
    setGridAnalysisAreaErrorMessage('*please define an analysis area');
  };

  const clearValidationForGridCoordinateSystem = () => {
    setIsGridCoordinateSystemValid(true);
  };

  const clearValidationForGridTerrainFile = () => {
    setIsGridTerrainFileValid(false);
    setGridTerrainFileErrorMessage('Please upload a file');
  };

  const clearValidationForGridSceneFile = () => {
    setIsGridSceneFileValid(false);
    setGridSceneFileErrorMessage('Please upload a file');
  };

  const clearValidationForGeodataName = () => {
    setIsGeodataNameValid(true);
    setGeodataNameErrorMessage('');
  };

  const clearValidationForGeodataCustomPolygon = () => {
    setIsGeodataCustomPolygonValid(false);
    setGeodataCustomPolygonErrorMessage('Please draw the coordinates');
  };

  const clearValidationForSimulationName = () => {
    setIsSimulationNameValid(true);
    setSimulationNameErrorMessage('');
  };

  const clearValidationForSimulationNoiseSources = () => {
    setIsSimulationNoiseSourcesValid(false);
    setSimulationNoiseSourcesErrorMessage('Please select min one noise source');
  };

  const clearValidationForSimulationWindSimulations = () => {
    setIsSimulationWindSimulationsValid(true);
    setSimulationWindSimulationsErrorMessage('');
  };

  return (
    <ValidationContext.Provider
      value={{
        isProjectNameValid,
        setIsProjectNameValid,
        projectNameErrorMessage,
        setProjectNameErrorMessage,
        isProjectMunicipalityValid,
        setIsProjectMunicipalityValid,
        projectMunicipalityErrorMessage,
        setProjectMunicipalityErrorMessage,
        showProjectValidationErrors,
        setShowProjectValidationErrors,
        clearValidationForProjectName,
        clearValidationForProjectMunicipality,
        clearProjectValidationErrors,

        isGridNameValid,
        setIsGridNameValid,
        gridNameErrorMessage,
        setGridNameErrorMessage,
        isGridAnalysisAreaValid,
        setIsGridAnalysisAreaValid,
        gridAnalysisAreaErrorMessage,
        setGridAnalysisAreaErrorMessage,
        isGridCoordinateSystemValid,
        setIsGridCoordinateSystemValid,
        clearValidationForGridName,
        clearValidationForGridAnalysisArea,
        clearValidationForGridCoordinateSystem,

        isGridTerrainFileValid,
        setIsGridTerrainFileValid,
        gridTerrainFileErrorMessage,
        setGridTerrainFileErrorMessage,
        clearValidationForGridTerrainFile,

        isGridSceneFileValid,
        setIsGridSceneFileValid,
        gridSceneFileErrorMessage,
        setGridSceneFileErrorMessage,
        clearValidationForGridSceneFile,

        isGeodataNameValid,
        setIsGeodataNameValid,
        geodataNameErrorMessage,
        setGeodataNameErrorMessage,

        isGeodataCustomPolygonValid,
        setIsGeodataCustomPolygonValid,
        geodataCustomPolygonErrorMessage,
        setGeodataCustomPolygonErrorMessage,

        clearValidationForGeodataName,
        clearValidationForGeodataCustomPolygon,

        isSimulationNameValid,
        setIsSimulationNameValid,
        simulationNameErrorMessage,
        setSimulationNameErrorMessage,
        isSimulationNoiseSourcesValid,
        setIsSimulationNoiseSourcesValid,
        simulationNoiseSourcesErrorMessage,
        setSimulationNoiseSourcesErrorMessage,
        clearValidationForSimulationName,
        clearValidationForSimulationNoiseSources,
        isSimulationWindSimulationsValid,
        setIsSimulationWindSimulationsValid,
        simulationWindSimulationsErrorMessage,
        setSimulationWindSimulationsErrorMessage,
        clearValidationForSimulationWindSimulations,
      }}
    >
      {props.children}
    </ValidationContext.Provider>
  );
};

export default ValidationContextProvider;
